import { Injectable, signal, computed, inject, DestroyRef } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { ConfigService, DropdownSizes } from '../../core/config.service';

export interface Inspector {
  userId: number;
  name: string;
  initials: string | null;
}

export interface StreetType {
  streetType: string;
  streetTypeDesc: string;
  activeFlag: string;
}

export interface Direction {
  directionCode: string;
  directionDesc: string;
  activeFlag: string;
}

export interface ActionLookup {
  actionCode: string;
  actionDesc: string;
  activeFlag: string;
  actionType: string;
  sqlStatement: string | null;
  fileName: string | null;
  confidentialFlag: string | null;
  firstContactFlag: string | null;
  firstWrittenContactFlag: string | null;
  hearingBoardFlag: string | null;
  hearingBoardOtherFlag: string | null;
  mailToResponsibleParties: string | null;
  mailToComplainants: string | null;
  automailingTimingFlag: string | null;
  automailingProgramCode: string | null;
  complainantCode1: string | null;
  complainantCode2: string | null;
  complainantCode3: string | null;
  complainantCode4: string | null;
  relationshipCode1: string | null;
  relationshipCode2: string | null;
  relationshipCode3: string | null;
  relationshipCode4: string | null;
  avgTimeGc: number | null;
  avgTimeVa: number | null;
  voluntaryCompFlag: string | null;
}

export interface Program {
  programCode: string;
  programDesc: string;
  activeFlag: string;
}

export const DIRECTIONS: Direction[] = [
  { directionCode: 'E', directionDesc: 'East', activeFlag: 'Y' },
  { directionCode: 'N', directionDesc: 'North', activeFlag: 'Y' },
  { directionCode: 'S', directionDesc: 'South', activeFlag: 'Y' },
  { directionCode: 'W', directionDesc: 'West', activeFlag: 'Y' },
];

interface PagedResponse<T> {
  data: T[];
  totalRecords?: number;
}

interface ProgramResponse {
  data: Program[];
}

interface PagedLookupConfig {
  endpoint: string;
  /** Request body field that carries the search term (type / streetType / name). */
  termField: string;
  sizeKey: keyof DropdownSizes;
  fallbackSize: number;
}

/**
 * Reactive state of a single lookup list: the loaded items plus the flags the
 * dropdowns bind to (initial load, "load more" spinner, whether pages remain).
 *
 * `totalRecords` from the API is the single source of truth for `hasMore`.
 * When a response has no total, more pages are assumed until a page comes back
 * empty.
 */
function createLookupState<T>() {
  const data = signal<T[]>([]);
  const loading = signal(false);
  const loadingMore = signal(false);
  const totalRecords = signal(0);
  const hasMore = signal(false);
  const term = signal('');
  const page = signal(1);

  const settle = (total: number) => {
    totalRecords.set(total);
    hasMore.set(total > 0 ? data().length < total : data().length > 0);
    loading.set(false);
    loadingMore.set(false);
  };

  return {
    data: data.asReadonly(),
    loading: loading.asReadonly(),
    loadingMore: loadingMore.asReadonly(),
    totalRecords: totalRecords.asReadonly(),
    hasMore: hasMore.asReadonly(),
    term: term.asReadonly(),

    /** Replace the whole list - a complete result, so nothing left to load. */
    set: (items: T[]) => {
      data.set(items);
      settle(items.length);
    },

    /** Store one response page: page 1 replaces, later pages append. */
    applyPage: (response: PagedResponse<T>, searchTerm: string, requestedPage: number) => {
      const items = response.data ?? [];
      data.update((current) => (requestedPage === 1 ? items : [...current, ...items]));
      term.set(searchTerm);
      page.set(requestedPage);
      settle(response.totalRecords ?? 0);
    },

    startLoading: (more: boolean) => {
      loading.set(true);
      loadingMore.set(more);
    },

    endLoading: () => {
      loading.set(false);
      loadingMore.set(false);
    },

    /** Search term and page for the next "load more" request. */
    nextPage: () => ({ term: term(), page: page() + 1 }),
  };
}

type LookupState<T> = ReturnType<typeof createLookupState<T>>;

@Injectable({
  providedIn: 'root',
})
export class LookUpService {
  private readonly http = inject(HttpClient);
  private readonly configService = inject(ConfigService);
  private readonly destroyRef = inject(DestroyRef);

  readonly streetTypeSearch = createLookupState<StreetType>();
  readonly actionSearch = createLookupState<ActionLookup>();
  readonly inspectorSearch = createLookupState<Inspector>();
  readonly directionSearch = createLookupState<Direction>();
  readonly programs = createLookupState<Program>();

  private readonly actionLookup = this.createPagedLookup(this.actionSearch, {
    endpoint: 'getActions',
    termField: 'type',
    sizeKey: 'actionType',
    fallbackSize: 10,
  });

  private readonly streetTypeLookup = this.createPagedLookup(this.streetTypeSearch, {
    endpoint: 'getStreetTypes',
    termField: 'streetType',
    sizeKey: 'streetType',
    fallbackSize: 100,
  });

  private readonly inspectorLookup = this.createPagedLookup(this.inspectorSearch, {
    endpoint: 'getInspectors',
    termField: 'name',
    sizeKey: 'inspector',
    fallbackSize: 60,
  });

  private programsLoaded = false;

  readonly activePrograms = computed(() =>
    this.programs
      .data()
      .filter((p) => p.activeFlag === 'Y')
      .sort((a, b) => a.programDesc.localeCompare(b.programDesc)),
  );

  constructor() {
    this.directionSearch.set(DIRECTIONS);
  }

  /** Program list is loaded once on workspace resolve - it is not paginated. */
  loadPrograms(force = false): Observable<Program[]> {
    if (this.programsLoaded && !force) {
      return of(this.programs.data());
    }

    this.programs.startLoading(false);

    return this.http.get<ProgramResponse>(`${this.configService.get().APIBaseUrl}/lookup/getPrograms`).pipe(
      map((response) => response.data),
      tap((items) => {
        this.programs.set(items);
        this.programsLoaded = true;
      }),
      catchError((err) => {
        console.error('LookUpService.loadPrograms error:', err);
        this.programs.endLoading();
        return of([]);
      }),
    );
  }

  searchActions(searchTerm: string): void {
    this.actionLookup.search(searchTerm);
  }

  loadMoreActions(): void {
    this.actionLookup.loadMore();
  }

  searchStreetTypes(searchTerm: string): void {
    this.streetTypeLookup.search(searchTerm);
  }

  loadMoreStreetTypes(): void {
    this.streetTypeLookup.loadMore();
  }

  searchInspectors(searchTerm: string): void {
    this.inspectorLookup.search(searchTerm);
  }

  loadMoreInspectors(): void {
    this.inspectorLookup.loadMore();
  }

  loadInitialWorkspaceLookups(): Observable<unknown> {
    return forkJoin({
      actions: this.actionLookup.loadInitial(),
      inspectors: this.inspectorLookup.loadInitial(),
      streetTypes: this.streetTypeLookup.loadInitial(),
    });
  }

  /** Directions are a fixed list filtered locally - no request involved. */
  searchDirections(searchTerm: string): void {
    const term = (searchTerm ?? '').trim().toLowerCase();

    this.directionSearch.set(
      term
        ? DIRECTIONS.filter(
            (d) =>
              d.directionCode.toLowerCase().includes(term) ||
              d.directionDesc.toLowerCase().includes(term),
          )
        : DIRECTIONS,
    );
  }

  
  private createPagedLookup<T>(state: LookupState<T>, config: PagedLookupConfig) {
    let hasLoaded = false;

    const fetchPage = (term: string, page: number): Observable<PagedResponse<T>> => {
      const body = {
        page,
        size: this.dropdownSize(config.sizeKey, config.fallbackSize),
        [config.termField]: term,
      };

      state.startLoading(page > 1);

      return this.http
        .post<PagedResponse<T>>(`${this.configService.get().APIBaseUrl}/lookup/${config.endpoint}`, body)
        .pipe(
          tap((response) => {
            state.applyPage(response, term, page);
            if (page === 1) {
              hasLoaded = true;
            }
          }),
          catchError((err) => {
            console.error(`LookUpService.${config.endpoint} error:`, err);
            if (page === 1) {
              state.set([]);
              hasLoaded = true;
            }
            else state.endLoading();
            return of<PagedResponse<T>>({ data: [] });
          }),
        );
    };

    const loadInitial = (): Observable<PagedResponse<T>> =>
      hasLoaded && state.term() === ''
        ? of({ data: state.data(), totalRecords: state.totalRecords() })
        : fetchPage('', 1);

    const search$ = new Subject<string>();
    const subscription = search$.pipe(switchMap((term) => fetchPage(term, 1))).subscribe();
    this.destroyRef.onDestroy(() => subscription.unsubscribe());

    return {
      search: (term: string) => {
        const next = term ?? '';
        if (hasLoaded && next === state.term() && !state.loading()) {
          return;
        }
        search$.next(next);
      },
      loadInitial,
      loadMore: () => {
        if (state.loading() || !state.hasMore()) return;
        const { term, page } = state.nextPage();
        fetchPage(term, page).subscribe();
      },
    };
  }

  private dropdownSize(key: keyof DropdownSizes, fallback: number): number {
    return this.configService.get().dropdownSizes?.[key] ?? fallback;
  }
}
